/**
 * Кнопка «написать нам»: открытие и закрытие панели.
 *
 * Всё, что здесь есть, — переключение атрибута open и обслуживание
 * клавиатуры. Никаких чужих скриптов: виджеты живого чата тянут
 * по мегабайту с чужих доменов, а у главной и так 453 запроса
 * и 8,5 секунды до полной загрузки.
 *
 * Панель прячется атрибутом hidden, а не удалением из разметки:
 * ссылки в ней — обычные <a>, и поисковику видно, куда они ведут.
 */
if (!customElements.get('contact-widget')) {
  customElements.define(
    'contact-widget',
    class ContactWidget extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.contact-widget__toggle');
        this.panel = this.querySelector('.contact-widget__panel');

        if (!this.toggle || !this.panel) return;

        this.toggle.addEventListener('click', () => this.switch());

        // Клавиша Esc закрывает панель — привычно и ожидаемо.
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.hasAttribute('open')) {
            this.close();
            this.toggle.focus();
          }
        });

        // Нажатие мимо виджета закрывает панель. Слушаем на документе,
        // но только пока панель открыта, чтобы не висеть зря.
        this.onOutside = (e) => {
          if (this.contains(e.target)) return;

          // Кнопка «помощь» в нижнем меню лежит вне виджета, но
          // закрывать по ней нельзя: её обрабатывает переключатель
          // ниже. Иначе одно нажатие закрыло бы панель и тут же
          // открыло снова.
          if (e.target.closest && e.target.closest('[data-contact-widget-open]')) return;

          this.close();
        };

        this.place();

        // Панель покупки выезжает и прячется при прокрутке, поэтому
        // пересчитываем на прокрутку и на смену размера окна. Считаем
        // не чаще кадра: слушатель прокрутки, который меряет раскладку
        // на каждое событие, — верный способ подтормозить страницу.
        let ждём = false;
        const пересчитать = () => {
          if (ждём) return;
          ждём = true;
          requestAnimationFrame(() => {
            ждём = false;
            this.place();
          });
        };

        window.addEventListener('scroll', пересчитать, { passive: true });
        window.addEventListener('resize', пересчитать, { passive: true });

        // Панель открывается и снаружи: на телефоне плавающей кнопки
        // нет, вместо неё пункт «помощь» в нижнем меню. Слушаем на
        // документе, чтобы работали и кнопки, появившиеся позже.
        document.addEventListener('click', (e) => {
          const внешняя = e.target.closest('[data-contact-widget-open]');
          if (!внешняя) return;

          e.preventDefault();
          this.switch();
        });
      }

      /**
       * Поднимает кнопку над закреплёнными полосами внизу экрана.
       *
       * Меряем живые элементы, а не переменные темы: --sticky-atc-bar-height
       * тема задаёт, а --mobile-sticky-bar-height не задаёт нигде, и расчёт
       * по ней молча брал ноль. Кнопка не налезала на навигацию только
       * по случайности — из-за того, что вместо её высоты подставлялась
       * высота другой полосы.
       *
       * Учитываем только то, что действительно занимает место внизу
       * экрана сейчас: панель покупки существует всегда, но большую
       * часть времени спрятана за нижним краем.
       */
      place() {
        const полосы = ['.mobile-sticky-bar', '.sticky-atc-bar'];
        const высотаОкна = window.innerHeight;

        // Ищем самую верхнюю границу среди занятых полос, а не сумму
        // их высот: полосы стоят одна над другой, и если однажды
        // встанут внахлёст, сумма завысит отступ и кнопка улетит
        // на середину экрана.
        let граница = высотаОкна;

        for (const селектор of полосы) {
          const el = document.querySelector(селектор);
          if (!el) continue;

          const cs = getComputedStyle(el);
          if (cs.display === 'none' || cs.visibility === 'hidden') continue;

          const b = el.getBoundingClientRect();
          // Полоса за нижним краем окна места не занимает.
          if (b.top >= высотаОкна || b.height < 1) continue;

          граница = Math.min(граница, b.top);
        }

        this.style.setProperty('--contact-widget-offset', Math.round(высотаОкна - граница) + 'px');
      }

      switch() {
        if (this.hasAttribute('open')) this.close();
        else this.open();
      }

      open() {
        this.setAttribute('open', '');
        this.panel.hidden = false;
        this.toggle.setAttribute('aria-expanded', 'true');

        // Слушателя вешаем следующим кадром: иначе то же самое нажатие,
        // которое открыло панель, тут же её и закроет.
        requestAnimationFrame(() => {
          document.addEventListener('click', this.onOutside);
        });

        const first = this.panel.querySelector('a');
        if (first) first.focus();
      }

      close() {
        if (!this.hasAttribute('open')) return;

        this.removeAttribute('open');
        this.toggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onOutside);

        // hidden ставим после перехода, иначе панель исчезает рывком.
        const finish = () => {
          if (!this.hasAttribute('open')) this.panel.hidden = true;
        };

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
        else setTimeout(finish, 200);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOutside);
      }
    }
  );
}
